import { __read, __rest, __assign } from 'tslib';
import * as React from 'react';
import { classNames } from 'uitk-react-utils';
import { getMonthYear } from '../generic-utils/dateUtils.esm.js';
import { UitkBaseCalendar } from '../CalendarBase/uitk-calendar.esm.js';
import styles from '../uitk-calendar.css';

var getClassNameList = classNames.bind(styles);
var getClosestScrollableParent = function (element) {
    var parent = element === null || element === void 0 ? void 0 : element.parentElement;
    while (parent) {
        var overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
};
var getMaxNumNextMonths = function (month, year, maxDate) {
    // Get the maximum number of months to load, including start and end months
    var numMonths;
    var monthDate = new Date(year, month, 1);
    numMonths = (maxDate.getFullYear() - monthDate.getFullYear()) * 12;
    numMonths -= monthDate.getMonth();
    numMonths += maxDate.getMonth() + 1;
    return numMonths <= 0 ? 0 : numMonths;
};
/**
 * This function is used to get the initial number of months to load
 * when the component mounts.
 * This function also handles the case when the selection is in the past or future.
 */
var getInitialPrevNextNumMonths = function (monthYear, selectionStart, numMonths, minDate) {
    var monthDate = new Date(monthYear.year, monthYear.month, 1);
    var selectedDateArr = selectionStart === null || selectionStart === void 0 ? void 0 : selectionStart.split('-');
    var isSelected = (selectedDateArr === null || selectedDateArr === void 0 ? void 0 : selectedDateArr.length) === 3;
    var correctedMinDate = minDate ? new Date(minDate.getFullYear(), minDate.getMonth(), 1) : null;
    var numPrevMonths = correctedMinDate && correctedMinDate < monthDate ? 1 : 0;
    var numNextMonths = numMonths - numPrevMonths;
    if (isSelected) {
        var selectedYear = Number(selectedDateArr[0]);
        var selectedMonth = Number(selectedDateArr[1]) - 1;
        var selDate = new Date(selectedYear, selectedMonth, 1);
        var isSelectionInPastMonth = selDate < monthDate;
        var isSelectionInFutureMonth = selDate > monthDate;
        if (isSelectionInPastMonth) {
            numPrevMonths =
                monthDate.getMonth() - selDate.getMonth() + 12 * (monthDate.getFullYear() - selDate.getFullYear());
            var oneMorePreviousMonth = correctedMinDate && correctedMinDate < selDate ? 1 : 0;
            return {
                numPrevMonths: numPrevMonths + oneMorePreviousMonth,
                numNextMonths: 1
            };
        }
        if (isSelectionInFutureMonth) {
            numNextMonths =
                selDate.getMonth() - monthDate.getMonth() + 12 * (selDate.getFullYear() - monthDate.getFullYear()) + 1;
            numPrevMonths = numNextMonths >= numMonths - 1 ? 0 : numPrevMonths;
            return {
                numPrevMonths: numPrevMonths,
                numNextMonths: numNextMonths < numMonths ? numMonths - numPrevMonths : numNextMonths
            };
        }
    }
    return { numPrevMonths: numPrevMonths, numNextMonths: numNextMonths };
};
var getNumMonths = function (month, year) { return year * 12 + month + 1; };
var getMaxNumPrevMonths = function (month, year, minDate) {
    if (minDate === void 0) { minDate = new Date(); }
    var minDateYear = minDate.getFullYear();
    var minDateMonth = minDate.getMonth();
    var getNumMinMonths = getNumMonths(minDateMonth, minDateYear);
    var getNumCurrentMonths = getNumMonths(month, year);
    var monthDiff = getNumCurrentMonths - getNumMinMonths;
    return monthDiff < 0 ? 0 : monthDiff;
};
var deleteMonthsFromMonthDate = function (year, month, numMonths) {
    var numDateMonths = getNumMonths(month, year);
    var diffNumMonths = numDateMonths - numMonths;
    var updatedYear = Math.floor(diffNumMonths / 12);
    var updatedMonth = (diffNumMonths % 12) - 1;
    return { updatedYear: updatedYear, updatedMonth: updatedMonth };
};
var UitkCalendarNavigationScrolling = function (props) {
    var elem = React.useRef(null);
    var _a = getMonthYear(), month = _a.month, year = _a.year;
    var stretchy = props.stretchy, selectionStart = props.selectionStart, _b = props.numMonthsToDisplay, numMonthsToDisplay = _b === void 0 ? 4 : _b, maxDate = props.maxDate, minDate = props.minDate, _c = props.padWeeks, padWeeks = _c === void 0 ? false : _c;
    var _d = __read(React.useState(function () {
        return getInitialPrevNextNumMonths({ month: month, year: year }, selectionStart, 
        // We need atleast 4 months initially loaded to ensure
        // a scroll overflow is created to enable infinite scroll
        numMonthsToDisplay > 4 ? numMonthsToDisplay : 4, minDate);
    }), 2), _e = _d[0], numPrevMonths = _e.numPrevMonths, numNextMonths = _e.numNextMonths, setNumPrevNextMonths = _d[1];
    var maxLoadableNextMonths = maxDate ? getMaxNumNextMonths(month, year, maxDate) : null;
    var maxLoadablePrevMonths = getMaxNumPrevMonths(month, year, minDate);
    var className = props.className, locale = props.locale, rest = __rest(props, ["className", "locale"]);
    var rootClassNames = getClassNameList('uitk-calendar-navigation-scrolling', className);
    var _f = deleteMonthsFromMonthDate(year, month, numPrevMonths), startYear = _f.updatedYear, startMonth = _f.updatedMonth;
    var numColumns = 1;
    var firstMonthRef = React.useRef(null);
    var secondMonthRef = React.useRef(null);
    var lastMonthRef = React.useRef(null);
    var observerRef = React.useRef(null);
    var currentMonth = React.useRef(null);
    var closestScrollableParent = React.useRef(null);
    // This ref is used to prevent scrolling to the current month when the component renders.
    var preventScrollOnDayFocus = React.useRef(false);
    // This effect is used to scroll to the current month when the component mounts.
    React.useEffect(function () {
        var _a, _b;
        (_b = (_a = currentMonth.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, []);
    // This callback is used to update the observer when the first month ref is set.
    var handleFirstMonthChange = React.useCallback(function (node) {
        var _a, _b;
        if (firstMonthRef.current)
            (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.unobserve(firstMonthRef.current);
        firstMonthRef.current = node;
        (_b = observerRef.current) === null || _b === void 0 ? void 0 : _b.observe(firstMonthRef.current);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstMonthRef.current]);
    var handleSecondMonthChange = React.useCallback(function (node) {
        secondMonthRef.current = node;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [secondMonthRef.current]);
    // This callback is used to update the observer when the last month ref is set.
    var handleLastMonthChange = React.useCallback(function (node) {
        var _a, _b;
        if (lastMonthRef.current)
            (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.unobserve(lastMonthRef.current);
        lastMonthRef.current = node;
        (_b = observerRef.current) === null || _b === void 0 ? void 0 : _b.observe(lastMonthRef.current);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastMonthRef.current]);
    /**
     * This effect is used to update the closest scrollable parent
     * when the element ref is set.
     */
    React.useEffect(function () {
        if (!closestScrollableParent.current && elem.current)
            closestScrollableParent.current = getClosestScrollableParent(elem.current);
    }, []);
    /**
     * This effect is used to update the number of months to load
     * when the user scrolls to the first or last month.
     */
    React.useEffect(function () {
        if (!observerRef.current && closestScrollableParent.current) {
            observerRef.current = new IntersectionObserver(function (entries) {
                entries.forEach(function (_a) {
                    var target = _a.target, isIntersecting = _a.isIntersecting;
                    if (target === lastMonthRef.current && isIntersecting) {
                        /**
                         * If the last month is intersecting, then we need to load
                         * one more month in the future.
                         */
                        setNumPrevNextMonths(function (prevNumPrevNextMonths) {
                            if (maxLoadableNextMonths === null || prevNumPrevNextMonths.numNextMonths < maxLoadableNextMonths) {
                                return {
                                    numPrevMonths: prevNumPrevNextMonths.numPrevMonths,
                                    numNextMonths: prevNumPrevNextMonths.numNextMonths + 1
                                };
                            }
                            return {
                                numPrevMonths: prevNumPrevNextMonths.numPrevMonths,
                                numNextMonths: prevNumPrevNextMonths.numNextMonths
                            };
                        });
                    }
                    else if (target === firstMonthRef.current && isIntersecting) {
                        /**
                         * If the first month is intersecting, then we need to load
                         * one more month in the past.
                         */
                        setNumPrevNextMonths(function (prevNumPrevNextMonths) {
                            var _a;
                            if (prevNumPrevNextMonths.numPrevMonths < maxLoadablePrevMonths) {
                                preventScrollOnDayFocus.current = true;
                                /**
                                 * We need to scroll to the second month in the list
                                 * to prevent infinite loop of loading months.
                                 */
                                (_a = secondMonthRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView();
                                return {
                                    numPrevMonths: prevNumPrevNextMonths.numPrevMonths + 1,
                                    numNextMonths: prevNumPrevNextMonths.numNextMonths
                                };
                            }
                            return {
                                numPrevMonths: prevNumPrevNextMonths.numPrevMonths,
                                numNextMonths: prevNumPrevNextMonths.numNextMonths
                            };
                        });
                    }
                });
            }, {
                threshold: 0.8,
                root: closestScrollableParent.current,
                rootMargin: '10% 0px 20% 0px'
            });
            // Observe the first and last month
            if (firstMonthRef.current && lastMonthRef.current) {
                observerRef.current.observe(firstMonthRef.current);
                observerRef.current.observe(lastMonthRef.current);
            }
        }
        return function () {
            // Cleanup observer on unmount
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [observerRef, closestScrollableParent]);
    return (React.createElement("div", { className: rootClassNames, ref: elem, "data-testid": "calendar-scrolling" },
        React.createElement(UitkBaseCalendar, __assign({}, rest, { stretchy: stretchy, locale: locale, maxDate: maxDate, month: startMonth, numColumns: numColumns, numMonthsToDisplay: numPrevMonths + numNextMonths, padWeeks: padWeeks, year: startYear, scrollToMonth: true, scrollToMonthRef: currentMonth, firstMonthCallback: handleFirstMonthChange, secondMonthCallback: handleSecondMonthChange, lastMonthCallback: handleLastMonthChange, preventScrollOnDayFocus: preventScrollOnDayFocus }))));
};

export { UitkCalendarNavigationScrolling };
